"use client";

import "@assistant-ui/react-markdown/styles/dot.css";

import {
  type CodeHeaderProps,
  MarkdownTextPrimitive,
  unstable_memoizeMarkdownComponents as memoizeMarkdownComponents,
  useIsMarkdownCodeBlock,
} from "@assistant-ui/react-markdown";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import { type FC, type ComponentProps, memo, useState } from "react";
import { CheckIcon, CopyIcon } from "lucide-react";

import { TooltipIconButton } from "./tooltip-icon-button";
import { cn } from "@/lib/utils";
import { sanitizationSchema } from "./markdown-sanitization";

const MarkdownTextImpl: FC<ComponentProps<typeof MarkdownTextPrimitive>> = ({ className, components, ...props }) => {
  return (
    <MarkdownTextPrimitive
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[
        rehypeRaw,
        [rehypeSanitize, sanitizationSchema],
      ]}
      className={cn("aui-md max-w-full min-w-0 wrap-break-word overflow-wrap-anywhere", className)}
      components={{ ...defaultComponents, ...components }}
      {...props}
    />
  );
};

export const MarkdownText = memo(MarkdownTextImpl);

const CodeHeader: FC<CodeHeaderProps> = ({ language, code }) => {
  const { isCopied, copyToClipboard } = useCopyToClipboard();
  const onCopy = () => {
    if (!code || isCopied) return;
    copyToClipboard(code);
  };

  return (
    <div className="mt-4 flex items-center justify-between gap-4 rounded-t-lg bg-zinc-900 px-4 py-2 text-sm font-semibold text-white">
      <span className="lowercase [&>span]:text-xs">{language}</span>
      <TooltipIconButton tooltip="Copy" onClick={onCopy}>
        {!isCopied && <CopyIcon />}
        {isCopied && <CheckIcon />}
      </TooltipIconButton>
    </div>
  );
};

const useCopyToClipboard = ({
  copiedDuration = 3000,
}: {
  copiedDuration?: number;
} = {}) => {
  const [isCopied, setIsCopied] = useState<boolean>(false);

  const copyToClipboard = (value: string) => {
    if (!value) return;

    navigator.clipboard.writeText(value).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), copiedDuration);
    });
  };

  return { isCopied, copyToClipboard };
};

const defaultComponents = memoizeMarkdownComponents({
  h1: ({ className, ...props }) => (
    <h1 className={cn("mb-8 scroll-m-20 text-4xl font-extrabold tracking-tight last:mb-0", className)} {...props} />
  ),
  h2: ({ className, ...props }) => (
    <h2 className={cn("mb-4 mt-8 scroll-m-20 text-3xl font-semibold tracking-tight first:mt-0 last:mb-0", className)} {...props} />
  ),
  h3: ({ className, ...props }) => (
    <h3 className={cn("mb-4 mt-6 scroll-m-20 text-2xl font-semibold tracking-tight first:mt-0 last:mb-0", className)} {...props} />
  ),
  h4: ({ className, ...props }) => (
    <h4 className={cn("mb-4 mt-6 scroll-m-20 text-xl font-semibold tracking-tight first:mt-0 last:mb-0", className)} {...props} />
  ),
  h5: ({ className, ...props }) => (
    <h5 className={cn("my-4 text-lg font-semibold first:mt-0 last:mb-0", className)} {...props} />
  ),
  h6: ({ className, ...props }) => (
    <h6 className={cn("my-4 font-semibold first:mt-0 last:mb-0", className)} {...props} />
  ),
  p: ({ className, ...props }) => (
    <p className={cn("mb-5 mt-5 leading-7 first:mt-0 last:mb-0", className)} {...props} />
  ),
  a: ({ className, ...props }) => (
    <a className={cn("text-primary font-medium underline underline-offset-4", className)} {...props} />
  ),
  blockquote: ({ className, ...props }) => (
    <blockquote className={cn("border-l-2 pl-6 italic", className)} {...props} />
  ),
  ul: ({ className, ...props }) => (
    <ul className={cn("my-5 ml-6 list-disc [&>li]:mt-2", className)} {...props} />
  ),
  ol: ({ className, ...props }) => (
    <ol className={cn("my-5 ml-6 list-decimal [&>li]:mt-2", className)} {...props} />
  ),
  hr: ({ className, ...props }) => (
    <hr className={cn("my-5 border-b", className)} {...props} />
  ),
  table: ({ className, ...props }) => (
    <div className="my-5 overflow-x-auto">
      <table className={cn("w-full border-separate border-spacing-0", className)} {...props} />
    </div>
  ),
  th: ({ className, ...props }) => (
    <th className={cn("bg-muted px-4 py-2 text-left font-bold first:rounded-tl-lg last:rounded-tr-lg [&[align=center]]:text-center [&[align=right]]:text-right", className)} {...props} />
  ),
  td: ({ className, ...props }) => (
    <td className={cn("border-b border-l px-4 py-2 text-left last:border-r [&[align=center]]:text-center [&[align=right]]:text-right", className)} {...props} />
  ),
  tr: ({ className, ...props }) => (
    <tr className={cn("m-0 border-b p-0 first:border-t [&:last-child>td:first-child]:rounded-bl-lg [&:last-child>td:last-child]:rounded-br-lg", className)} {...props} />
  ),
  sup: ({ className, ...props }) => (
    <sup className={cn("[&>a]:text-xs [&>a]:no-underline", className)} {...props} />
  ),
  pre: ({ className, ...props }) => (
    <div className="overflow-x-auto">
      <pre className={cn("rounded-t-none! rounded-b-lg bg-black p-4 text-white min-w-0", className)} {...props} />
    </div>
  ),
  code: function Code({ className, ...props }) {
    const isCodeBlock = useIsMarkdownCodeBlock();
    return (
      <code
        className={cn(!isCodeBlock && "bg-muted rounded border font-semibold", className)}
        {...props}
      />
    );
  },
  CodeHeader,
});

export const StandaloneMarkdownText: FC<{ className?: string; children: string }>= ({ className, children }) => {
  const reactMarkdownComponents = {
    h1: ({ className: cnProp, ...props }: any) => (
      <h1 className={cn("mb-8 scroll-m-20 text-4xl font-extrabold tracking-tight last:mb-0", cnProp)} {...props} />
    ),
    h2: ({ className: cnProp, ...props }: any) => (
      <h2 className={cn("mb-4 mt-8 scroll-m-20 text-3xl font-semibold tracking-tight first:mt-0 last:mb-0", cnProp)} {...props} />
    ),
    h3: ({ className: cnProp, ...props }: any) => (
      <h3 className={cn("mb-4 mt-6 scroll-m-20 text-2xl font-semibold tracking-tight first:mt-0 last:mb-0", cnProp)} {...props} />
    ),
    h4: ({ className: cnProp, ...props }: any) => (
      <h4 className={cn("mb-4 mt-6 scroll-m-20 text-xl font-semibold tracking-tight first:mt-0 last:mb-0", cnProp)} {...props} />
    ),
    h5: ({ className: cnProp, ...props }: any) => (
      <h5 className={cn("my-4 text-lg font-semibold first:mt-0 last:mb-0", cnProp)} {...props} />
    ),
    h6: ({ className: cnProp, ...props }: any) => (
      <h6 className={cn("my-4 font-semibold first:mt-0 last:mb-0", cnProp)} {...props} />
    ),
    p: ({ className: cnProp, ...props }: any) => (
      <p className={cn("mb-5 mt-5 leading-7 first:mt-0 last:mb-0", cnProp)} {...props} />
    ),
    a: ({ className: cnProp, ...props }: any) => (
      <a className={cn("text-primary font-medium underline underline-offset-4", cnProp)} {...props} />
    ),
    blockquote: ({ className: cnProp, ...props }: any) => (
      <blockquote className={cn("border-l-2 pl-6 italic", cnProp)} {...props} />
    ),
    ul: ({ className: cnProp, ...props }: any) => (
      <ul className={cn("my-5 ml-6 list-disc [&>li]:mt-2", cnProp)} {...props} />
    ),
    ol: ({ className: cnProp, ...props }: any) => (
      <ol className={cn("my-5 ml-6 list-decimal [&>li]:mt-2", cnProp)} {...props} />
    ),
    hr: ({ className: cnProp, ...props }: any) => (
      <hr className={cn("my-5 border-b", cnProp)} {...props} />
    ),
    table: ({ className: cnProp, ...props }: any) => (
      <div className="my-5 overflow-x-auto">
        <table className={cn("w-full border-separate border-spacing-0", cnProp)} {...props} />
      </div>
    ),
    th: ({ className: cnProp, ...props }: any) => (
      <th className={cn("bg-muted px-4 py-2 text-left font-bold first:rounded-tl-lg last:rounded-tr-lg [&[align=center]]:text-center [&[align=right]]:text-right", cnProp)} {...props} />
    ),
    td: ({ className: cnProp, ...props }: any) => (
      <td className={cn("border-b border-l px-4 py-2 text-left last:border-r [&[align=center]]:text-center [&[align=right]]:text-right", cnProp)} {...props} />
    ),
    tr: ({ className: cnProp, ...props }: any) => (
      <tr className={cn("m-0 border-b p-0 first:border-t [&:last-child>td:first-child]:rounded-bl-lg [&:last-child>td:last-child]:rounded-br-lg", cnProp)} {...props} />
    ),
    sup: ({ className: cnProp, ...props }: any) => (
      <sup className={cn("[&>a]:text-xs [&>a]:no-underline", cnProp)} {...props} />
    ),
    code: ({ inline, className: cnProp, children, ...props }: any) => {
      const codeString = String(children ?? "");
      if (inline) {
        return (
          <code className={cn("bg-muted rounded border font-semibold", cnProp)} {...props}>
            {children}
          </code>
        );
      }
      const match = /language-(\w+)/.exec(cnProp || "");
      const language = match?.[1] ?? "";
      return (
        <div className="mt-4 overflow-x-auto">
          <CodeHeader language={language} code={codeString} />
          <pre className={cn("rounded-t-none! rounded-b-lg bg-black p-4 text-white min-w-0", cnProp)}>
            <code className={cnProp} {...props}>{children}</code>
          </pre>
        </div>
      );
    },
  } as any;

  return (
    <div className={cn("aui-md max-w-full min-w-0 wrap-break-word overflow-wrap-anywhere", className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[
          rehypeRaw,
          [rehypeSanitize, sanitizationSchema],
        ]}
        components={reactMarkdownComponents}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
};
